/**
 * Hafif, sıfır-bağımlı i18n altyapısı.
 *
 * Kullanım:
 *   - Functional bileşen:  const { t, lang, setLang } = useT();   t('tickets.detail.title')
 *   - Class bileşen:        export default withT(MyComponent);    this.props.t('...')
 *
 * Çeviriler `locales/<code>.json` dosyalarında nested obje olarak tutulur.
 * `t('a.b.c')` nokta-yollu key resolve eder; bulamazsa fallback olarak `tr`
 * dener, o da yoksa key'in kendisini döner. {{var}} yer-tutucusu desteklenir.
 *
 * Override katmanı: `/Translations/All` endpoint'inden gelen DB override'ları
 * statik JSON'ların ÜSTÜNDE çalışır. Çözümleme sırası:
 *   1. overrides[lang][key]   ← admin DB üzerinden ezdiği değer
 *   2. lookup(DICTS[lang], key) ← statik locale JSON
 *   3. lookup(DICTS[tr], key)   ← TR fallback
 *   4. key (placeholder)
 *
 * Override map'i app start'ta fetch edilir, localStorage'a yazılır;
 * sonraki yüklemelerde önce cache'den apply edilir, arka planda taze sürüm
 * çekilir (stale-while-revalidate).
 *
 * Persian (fa) RTL — language değişince <html dir> set edilir.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import tr from './locales/tr.json';
import en from './locales/en.json';
import de from './locales/de.json';
import ru from './locales/ru.json';
import fa from './locales/fa.json';
import zh from './locales/zh.json';
import hi from './locales/hi.json';
import fr from './locales/fr.json';

const DICTS = { tr, en, de, ru, fa, zh, hi, fr };
const RTL_LANGS = new Set(['fa']);
const STORAGE_KEY = 'bonus.lang';
const OVERRIDES_KEY = 'bonus.i18n.overrides';
const DEFAULT_LANG = 'tr';

/** Endpoint kullanıcı login öncesi de erişilebilir olduğu için fetch ile direkt çağırıyoruz —
 * AxiosConfig token zorunluluğu var mı diye burada karışmaktan kaçınıyoruz. */
const fetchOverrides = async () => {
    try {
        const url = (process.env.REACT_APP_ADDRESS || '') + '/Translations/All';
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        });
        if (!res.ok) return null;
        const json = await res.json();
        const data = json?.payload?.status ? json.payload.data : null;
        return data && typeof data === 'object' ? data : null;
    } catch {
        return null;
    }
};

const loadCachedOverrides = () => {
    try {
        const raw = localStorage.getItem(OVERRIDES_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch { return null; }
};

const saveCachedOverrides = (data) => {
    try { localStorage.setItem(OVERRIDES_KEY, JSON.stringify(data || {})); } catch { /* yutulur */ }
};

export const SUPPORTED_LANGS = [
    { code: 'tr', label: 'Türkçe',     flag: '🇹🇷' },
    { code: 'en', label: 'English',    flag: '🇬🇧' },
    { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
    { code: 'ru', label: 'Русский',    flag: '🇷🇺' },
    { code: 'fa', label: 'فارسی',      flag: '🇮🇷' },
    { code: 'zh', label: '中文',        flag: '🇨🇳' },
    { code: 'hi', label: 'हिन्दी',       flag: '🇮🇳' },
    { code: 'fr', label: 'Français',   flag: '🇫🇷' },
];

const I18nContext = createContext({
    lang: DEFAULT_LANG,
    setLang: () => {},
    t: (k) => k,
});

const resolveInitialLang = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && DICTS[saved]) return saved;
    } catch { /* SSR/private mode */ }
    return DEFAULT_LANG;
};

const lookup = (dict, path) => {
    if (!dict) return undefined;
    const parts = path.split('.');
    let cur = dict;
    for (const p of parts) {
        if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
        else return undefined;
    }
    return typeof cur === 'string' ? cur : undefined;
};

const interpolate = (str, params) => {
    if (!params) return str;
    return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (params[k] ?? `{{${k}}}`));
};

export const I18nProvider = ({ children }) => {
    const [lang, setLangState] = useState(resolveInitialLang);
    // Override map: { tr: { 'loginForm.invalidCredentials': '...' }, en: {...}, ... }
    // Cold start için cache'den okunur — login ekranı dahi DB değeri görür.
    const [overrides, setOverrides] = useState(() => loadCachedOverrides() || {});

    useEffect(() => {
        const isRtl = RTL_LANGS.has(lang);
        document.documentElement.setAttribute('lang', lang);
        document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    }, [lang]);

    // App start'ta taze override'ları çek (stale-while-revalidate)
    useEffect(() => {
        let mounted = true;
        fetchOverrides().then((fresh) => {
            if (!mounted || !fresh) return;
            setOverrides(fresh);
            saveCachedOverrides(fresh);
        });
        return () => { mounted = false; };
    }, []);

    const setLang = useCallback((next) => {
        if (!DICTS[next]) return;
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* yutulur */ }
        setLangState(next);
    }, []);

    const t = useCallback((key, params) => {
        if (!key) return '';
        // 1) DB override (admin yönettiği)
        const langOv = overrides[lang];
        if (langOv && typeof langOv === 'object' && key in langOv && typeof langOv[key] === 'string') {
            return interpolate(langOv[key], params);
        }
        // 2) Statik dict — current lang
        const primary = lookup(DICTS[lang], key);
        if (primary !== undefined) return interpolate(primary, params);
        // 3) DB override — TR fallback
        const fbOv = overrides[DEFAULT_LANG];
        if (fbOv && typeof fbOv === 'object' && key in fbOv && typeof fbOv[key] === 'string') {
            return interpolate(fbOv[key], params);
        }
        // 4) Statik dict — TR fallback
        const fallback = lookup(DICTS[DEFAULT_LANG], key);
        if (fallback !== undefined) return interpolate(fallback, params);
        return key;
    }, [lang, overrides]);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useT = () => useContext(I18nContext);

/**
 * Class bileşenler için HOC. props.t, props.lang, props.setLang sağlar.
 */
export const withT = (Wrapped) => {
    const WithT = (props) => {
        const ctx = useContext(I18nContext);
        return <Wrapped {...props} t={ctx.t} lang={ctx.lang} setLang={ctx.setLang} />;
    };
    WithT.displayName = `withT(${Wrapped.displayName || Wrapped.name || 'Component'})`;
    return WithT;
};
